import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Copy, Mail, ShieldCheck } from "lucide-react";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [devUrl, setDevUrl] = useState("");
    const [expires, setExpires] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setDevUrl("");
        try {
            const { data } = await api.post(`/auth/forgot-password`, { email: email.trim().toLowerCase() });
            toast.success("If that email is registered, a reset link has been generated.");
            if (data?.dev_reset_url) {
                setDevUrl(data.dev_reset_url);
                setExpires(data.expires_at || "");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not process request");
        } finally {
            setLoading(false);
        }
    };

    const copyDevUrl = async () => {
        try {
            await navigator.clipboard.writeText(devUrl);
            toast.success("Reset link copied");
        } catch { toast.error("Could not copy"); }
    };

    return (
        <div className="min-h-screen grid place-items-center noise-bg px-6" data-testid="forgot-password-page">
            <form onSubmit={submit} className="w-full max-w-md rounded-2xl border bg-white p-8 shadow-sm animate-fade-up">
                <div className="mb-6 flex items-center gap-3">
                    <div className="grid h-11 w-11 place-items-center rounded-xl bg-rotary-blue text-white">
                        <ShieldCheck className="h-5 w-5" />
                    </div>
                    <div>
                        <span className="label-eyebrow">Password recovery</span>
                        <h1 className="rotary-heading text-2xl text-rotary-ink leading-tight">Forgot password</h1>
                    </div>
                </div>

                <p className="text-sm text-muted-foreground mb-5">
                    Enter your admin email. If the account exists, we&apos;ll generate a one-hour reset link.
                </p>

                <div className="space-y-4">
                    <div>
                        <Label htmlFor="email" className="label-eyebrow">Email</Label>
                        <div className="relative mt-2">
                            <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="admin@yourdomain.com"
                                className="pl-9 h-11"
                                data-testid="forgot-email-input"
                                autoComplete="email"
                                required
                            />
                        </div>
                    </div>

                    <Button
                        type="submit"
                        disabled={loading}
                        className="w-full h-11 rounded-full bg-rotary-blue text-white hover:bg-rotary-sky"
                        data-testid="forgot-submit-btn"
                    >
                        {loading ? "Sending…" : "Send reset link"}
                    </Button>
                </div>

                {devUrl && (
                    <div className="mt-5 rounded-xl border border-rotary-gold/50 bg-rotary-gold/10 p-4 space-y-2" data-testid="forgot-dev-url-box">
                        <p className="text-xs font-semibold text-rotary-ink uppercase tracking-wider">
                            Reset link (email service not yet enabled)
                        </p>
                        <p className="text-xs text-muted-foreground break-all font-mono">
                            {devUrl}
                        </p>
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="rounded-full"
                                onClick={copyDevUrl}
                                data-testid="forgot-copy-link-btn"
                            >
                                <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy link
                            </Button>
                            {expires && (
                                <span className="text-[11px] text-muted-foreground">Expires {new Date(expires).toLocaleString()}</span>
                            )}
                        </div>
                    </div>
                )}

                <Link
                    to="/admin/login"
                    className="mt-6 inline-flex items-center gap-1 text-sm text-rotary-blue hover:underline"
                    data-testid="forgot-back-to-login"
                >
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
                </Link>
            </form>
        </div>
    );
}
